<template>
  <v-form ref="form" v-model="isValid">
    <v-row>
      <v-col cols="12" md="12">
        <v-text-field
          v-model.trim="localForm.title"
          :label="$vuetify.lang.t('$vuetify.subscription_plans.title')"
          outlined
          :rules="[rules.required($vuetify.lang.t('$vuetify.subscription_plans.title'))]"
        />
      </v-col>

      <v-col cols="12" md="6">
        <v-text-field
          v-model.trim="localForm.product_id"
          :label="$vuetify.lang.t('$vuetify.subscription_plans.product_id')"
          outlined
          :rules="[rules.required($vuetify.lang.t('$vuetify.subscription_plans.product_id'))]"
        />
      </v-col>

      <v-col cols="12" md="6">
        <v-text-field
          v-model.trim="localForm.priceId"
          :label="$vuetify.lang.t('$vuetify.subscription_plans.price_id')"
          outlined
          :rules="[rules.required($vuetify.lang.t('$vuetify.subscription_plans.price_id'))]"
        />
      </v-col>

      <v-col cols="12" md="6">
        <v-text-field
          v-model="localForm.label_en"
          :label="$vuetify.lang.t('$vuetify.subscription_plans.label_en')"
          outlined
        >
          <template v-slot:append>
            <v-tooltip bottom max-width="350">
              <template v-slot:activator="{ on }">
                <span v-on="on" class="info-icon">i</span>
              </template>
              <span>{{ $vuetify.lang.t('$vuetify.subscription_plans.hints.label') }}</span>
            </v-tooltip>
          </template>
        </v-text-field>
      </v-col>

      <v-col cols="12" md="6">
        <v-text-field
          v-model="localForm.label_jp"
          :label="$vuetify.lang.t('$vuetify.subscription_plans.label_jp')"
          outlined
        >
          <template v-slot:append>
            <v-tooltip bottom max-width="350">
              <template v-slot:activator="{ on }">
                <span v-on="on" class="info-icon">i</span>
              </template>
              <span>{{ $vuetify.lang.t('$vuetify.subscription_plans.hints.label') }}</span>
            </v-tooltip>
          </template>
        </v-text-field>
      </v-col>

      <v-col cols="12" md="4">
        <v-text-field
          v-model.number="localForm.price"
          :label="$vuetify.lang.t('$vuetify.subscription_plans.price')"
          type="number"
          outlined
          @keydown.e.prevent
          @keydown.+.prevent
          @keydown.-.prevent
          :rules="[
            rules.required($vuetify.lang.t('$vuetify.subscription_plans.price')),
            rules.nonNegative($vuetify.lang.t('$vuetify.subscription_plans.price'))
          ]"
        />
      </v-col>

      <v-col cols="12" md="4">
        <v-text-field
          v-model.number="localForm.sequence"
          :label="$vuetify.lang.t('$vuetify.subscription_plans.sequence')"
          type="number"
          outlined
          @keydown.e.prevent
          @keydown.+.prevent
          @keydown.-.prevent    
          :rules="[
            rules.required($vuetify.lang.t('$vuetify.subscription_plans.sequence')),
            rules.nonNegative($vuetify.lang.t('$vuetify.subscription_plans.sequence')),
            rules.uniquePlanSequence
          ]"
        >
          <template v-slot:append>
            <v-tooltip bottom max-width="350">
              <template v-slot:activator="{ on }">
                <span v-on="on" class="info-icon">i</span>
              </template>
              <span>{{ $vuetify.lang.t('$vuetify.subscription_plans.hints.sequence') }}</span>
            </v-tooltip>
          </template>
        </v-text-field>
      </v-col>

      <v-col cols="12" md="4">
        <v-select
          v-model="localForm.status"
          :items="statusOptions"
          item-text="text"
          item-value="value"
          :label="$vuetify.lang.t('$vuetify.subscription_plans.status')"
          outlined
          :rules="[rules.required($vuetify.lang.t('$vuetify.subscription_plans.status'))]"
        />
      </v-col>

      <v-col cols="12">
        <v-textarea
          v-model="localForm.description_en"
          :label="$vuetify.lang.t('$vuetify.subscription_plans.description_en')"
          outlined
          rows="3"
        />
      </v-col>

      <v-col cols="12">
        <v-textarea
          v-model="localForm.description_jp"
          :label="$vuetify.lang.t('$vuetify.subscription_plans.description_jp')"
          outlined
          rows="3"
        />
      </v-col>

      <v-col cols="12" md="3">
        <v-select
          v-model="localForm.currency"
          :items="currencyOptions"
          item-text="text"
          item-value="value"
          :label="$vuetify.lang.t('$vuetify.subscription_plans.currency')"
          outlined
          :rules="[rules.required($vuetify.lang.t('$vuetify.subscription_plans.currency'))]"
        />
      </v-col>

      <v-col cols="12" md="3">
        <v-select
          v-model="localForm.interval"
          :items="intervalOptions"
          item-text="text"
          item-value="value"
          :label="$vuetify.lang.t('$vuetify.subscription_plans.billing_cycle')"
          outlined
          :rules="[rules.required($vuetify.lang.t('$vuetify.subscription_plans.billing_cycle'))]"
        />
      </v-col>

      <v-col cols="12" md="3">
        <v-select
          v-model="localForm.billingType"
          :items="billingTypeOptions"
          item-text="text"
          item-value="value"
          :label="$vuetify.lang.t('$vuetify.subscription_plans.billing_type')"
          outlined
        />
      </v-col>

      <v-col cols="12" md="3">
        <v-select
          v-model="localForm.billingScheme"
          :items="billingSchemeOptions"
          item-text="text"
          item-value="value"
          :label="$vuetify.lang.t('$vuetify.subscription_plans.billing_scheme')"
          outlined
        />
      </v-col>
    </v-row>

    <div class="d-flex justify-space-between align-center mb-3 mt-2">
      <div class="text-subtitle-1 font-weight-medium">
        {{ $vuetify.lang.t('$vuetify.subscription_plans.features') }}
      </div>

      <v-btn small outlined color="primary" @click="addFeature">
        {{ $vuetify.lang.t('$vuetify.subscription_plans.add_feature') }}
      </v-btn>
    </div>

    <v-card
      v-for="(feature, index) in localForm.features"
      :key="feature.uid"
      outlined 
      class="feature-item-card mb-4"
    >
      <div class="feature-card-header d-flex align-center justify-end px-4 py-3">

        <v-btn
          icon
          color="error"
          :disabled="localForm.features.length === 1"
          @click="removeFeature(index)"
        >
          <v-icon>mdi-delete</v-icon>
        </v-btn>
      </div>

      <v-card-text class="px-md-4 pb-md-0 pt-md-6">
        <v-row >
          <v-col cols="12" md="6">
            <v-combobox
              ref="featureKeyInputs"
              v-model="feature.key"
              :items="featureKeyOptions"
              :label="$vuetify.lang.t('$vuetify.subscription_plans.feature_key')"
              outlined
              clearable
              hide-selected
              :validate-on-blur="false"
              :rules="[
                rules.required($vuetify.lang.t('$vuetify.subscription_plans.feature_key')),
                rules.featureKeyFormat,
                rules.featureKey(index)
              ]"
              @update:search-input="value => onFeatureKeyTyping(index, value)"
            >
              <template v-slot:append>
                <v-tooltip bottom max-width="350">
                  <template v-slot:activator="{ on }">
                    <span v-on="on" class="info-icon">i</span>
                  </template>
                  <span>{{ $vuetify.lang.t('$vuetify.subscription_plans.hints.feature_key') }}</span>
                </v-tooltip>
              </template>
            </v-combobox>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              ref="featureSequenceInputs"
              v-model.number="feature.sequence"
              :label="$vuetify.lang.t('$vuetify.subscription_plans.feature_sequence')"
              type="number"
              @keydown.e.prevent
              @keydown.+.prevent
              @keydown.-.prevent
              outlined
              :rules="[
                rules.required($vuetify.lang.t('$vuetify.subscription_plans.feature_sequence')),
                rules.featureSequence(index),
                rules.nonNegative($vuetify.lang.t('$vuetify.subscription_plans.feature_sequence')),
              ]"
            >
              <template v-slot:append>
                <v-tooltip bottom max-width="350">
                  <template v-slot:activator="{ on }">
                    <span v-on="on" class="info-icon">i</span>
                  </template>
                  <span>{{ $vuetify.lang.t('$vuetify.subscription_plans.hints.feature_sequence') }}</span>
                </v-tooltip>
              </template>
            </v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-textarea
              v-model.trim="feature.description_en"
              :label="$vuetify.lang.t('$vuetify.subscription_plans.feature_description_en')"
              outlined
              rows="1"
              auto-grow
              :rules="[rules.required($vuetify.lang.t('$vuetify.subscription_plans.feature_description_en'))]"
            />
          </v-col>

          <v-col cols="12" md="6">
            <v-textarea
              v-model.trim="feature.description_jp"
              :label="$vuetify.lang.t('$vuetify.subscription_plans.feature_description_jp')"
              outlined
              rows="1"
              auto-grow
              :rules="[rules.required($vuetify.lang.t('$vuetify.subscription_plans.feature_description_jp'))]"
            />
          </v-col>

          <v-col cols="12" md="12" class="d-flex  justify-space-between pt-0 pb-8">
            <v-checkbox
              v-model="feature.enabled"
              hide-details
              class="ma-0"
            >
              <template v-slot:label>
                <div class="d-flex align-center">
                  <span>
                    {{ $vuetify.lang.t('$vuetify.subscription_plans.feature_enabled') }}
                  </span>

                  <v-tooltip bottom max-width="350">
                    <template v-slot:activator="{ on }">
                      <span v-on="on" class="info-icon ml-2">i</span>
                    </template>
                    <span>
                      {{ $vuetify.lang.t('$vuetify.subscription_plans.hints.feature_enabled') }}
                    </span>
                  </v-tooltip>
                </div>
              </template>
            </v-checkbox>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <div class="d-flex justify-end pt-0 mt-md-6">
      <v-btn
        color="error"
        outlined
        class="mr-2"
        @click="$emit('cancel')"
      >
        {{ $vuetify.lang.t('$vuetify.common.cancel') }}
      </v-btn>

      <v-btn color="primary" :loading="loading" @click="submit">
        {{ submitText }}
      </v-btn>
    </div>
  </v-form>
</template>

<script>
import {
  createEmptyPlanForm,
  clonePlanForm,
} from "@/helpers/subscriptionPlan";
import { v4 as uuidv4 } from 'uuid';
import validations from "@/helpers/validations"; 

const createEmptyFeature = (sequence = 1) => ({
  uid: uuidv4(),
  key: "",
  enabled: false,
  description_en: "",
  description_jp: "",
  sequence,
});

export default {
  name: "SubscriptionPlanForm",

  props: {
    initialData: {
      type: Object,
      default: () => createEmptyPlanForm(),
    },
    existingPlans: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    submitText: {
      type: String,
      default: "Save",
    },
  },

  data() {
    return {
      isValid: false,
      localForm: createEmptyPlanForm(),

      currencyOptions: [
        { text: "USD", value: "usd" },
      ],

      intervalOptions: [
        { text: this.$vuetify.lang.t("$vuetify.subscription_plans.options.billing_cycle.day"), value: "day" },
        { text: this.$vuetify.lang.t("$vuetify.subscription_plans.options.billing_cycle.week"), value: "week" },
        { text: this.$vuetify.lang.t("$vuetify.subscription_plans.options.billing_cycle.month"), value: "month" },
        { text: this.$vuetify.lang.t("$vuetify.subscription_plans.options.billing_cycle.year"), value: "year" },
      ],

      statusOptions: [
        { text: this.$vuetify.lang.t("$vuetify.subscription_plans.options.status.active"), value: "active" },
        { text: this.$vuetify.lang.t("$vuetify.subscription_plans.options.status.inactive"), value: "inactive" },
      ],

      billingTypeOptions: [
        { text: this.$vuetify.lang.t("$vuetify.subscription_plans.options.billing_type.recurring"), value: "recurring" },
      ],

      billingSchemeOptions: [
        { text: this.$vuetify.lang.t("$vuetify.subscription_plans.options.billing_scheme.per_unit"), value: "per_unit" }
      ],

      featureKeyOptions: [
        "is_conversation_adding",
        "is_image_context_enabled",
        "is_conversation_learning",
        "is_web_search_enabled",
        "is_code_execution_enabled",
      ],
    };
  },

  computed: {
    rules() {
      return {
        required: (property) => validations.requiredTrimmed(property),
        nonNegative: (property) => validations.nonNegative(property),
        
        uniquePlanSequence: (v) => {
          const value = String(v ?? "").trim();
          if (!value) return true;

          const normalizedValue = Number(value);
          const currentPlanId = this.localForm.id;

          const duplicateExists = this.existingPlans.some((plan) => {
            const sameSequence = Number(plan.sequence) === normalizedValue;
            const samePlan = plan?.id === currentPlanId;
            return sameSequence && !samePlan;
          });

          return (
            !duplicateExists ||
            this.$vuetify.lang.t("$vuetify.subscription_plans.validation.unique_sequence")
          );
        },

        featureKeyFormat: (v) => {
          const value = String(v ?? "").trim();
          if (!value) return true;

          return (
            /^[a-z0-9_]+$/.test(value) ||
            this.$vuetify.lang.t("$vuetify.subscription_plans.validation.feature_key_format")
          );
        },

        featureKey: (currentIndex) => (v) => {
          const value = String(v ?? "").trim();
          if (!value) return true;

          const duplicateCount = this.localForm.features.filter(
            (feature, index) =>
              index !== currentIndex &&
              String(feature.key ?? "").trim().toLowerCase() === value.toLowerCase()
          ).length;

          return (
            duplicateCount === 0 ||
            this.$vuetify.lang.t("$vuetify.subscription_plans.validation.unique_feature_key")
          );
        },

        featureSequence: (currentIndex) => (v) => {
          const value = String(v ?? "").trim();
          if (!value) return true;

          const normalizedValue = Number(value);

          const duplicateCount = this.localForm.features.filter(
            (feature, index) =>
              index !== currentIndex &&
              Number(feature.sequence) === normalizedValue
          ).length;

          return (
            duplicateCount === 0 ||
            this.$vuetify.lang.t("$vuetify.subscription_plans.validation.unique_sequence")
          );
        },
      };
    },
  },

  watch: {
    initialData: {
      immediate: true,
      deep: true,
      handler(newValue) {
        const formData = clonePlanForm(newValue);

        if (Array.isArray(formData.features)) {
          formData.features = [...formData.features].sort(
            (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)
          );
        }

        if (!formData.features.length) {
          formData.features = [createEmptyFeature(1)];
        }

        this.localForm = formData;
      },
    },

    "localForm.features": {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.$refs.featureKeyInputs?.forEach((input) => input.validate());
          this.$refs.featureSequenceInputs?.forEach((input) => input.validate());
        });
      },
    },
  },

  methods: {
    onFeatureKeyTyping(index, value) {
      this.$set(this.localForm.features[index], 'key', value ?? '');

      this.$nextTick(() => {
        const thisInput = this.$refs.featureKeyInputs?.[index];
        if (thisInput) thisInput.validate();

        this.$refs.featureKeyInputs?.forEach((input, i) => {
          if (i !== index) input.validate();
        });
      });
    },

    addFeature() {
      const maxSequence = this.localForm.features.reduce(
        (max, feature) => Math.max(max, Number(feature.sequence) || 0),
        0
      );

      this.localForm.features.push(
        createEmptyFeature(maxSequence + 1)
      );
    },

    removeFeature(index) {
      if (this.localForm.features.length === 1) return;

      this.localForm.features.splice(index, 1);

      this.$nextTick(() => {
        this.$refs.form.resetValidation();

        this.$refs.featureKeyInputs?.forEach((input) => input.validate());
        this.$refs.featureSequenceInputs?.forEach((input) => input.validate());
      });
    },

    normalizeFeatureKeys(features = []) {
      return features.map((feature) => ({
        ...feature,
        key:
          typeof feature.key === "object"
            ? feature.key.value || feature.key.text || ""
            : feature.key,
      }));
    },

    submit() {
      const isBaseFormValid = this.$refs.form.validate();
      if (!isBaseFormValid) return;

      const payload = clonePlanForm(this.localForm);
      payload.features = this.normalizeFeatureKeys(payload.features);

      this.$emit("submit", payload);
    },
  },
};
</script>

<style scoped>
.feature-item-card {
  border-radius: 10px;
  border-color: #e5e7eb;
  background: #ffffff;
}
.feature-card-header {
  background: #f9fafb;
  border-bottom: 1px solid #eef2f7;
}

</style>